import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { FormModal, ModalField, modalInputClass } from '@/components/ui/FormModal'
import { PageHero } from '@/components/ui/PageHero'
import { useToast } from '@/contexts/ToastContext'
import {
  formatCompact,
  formatCurrency,
  formatNumber,
  formatPercentChange,
} from '@/lib/format'
import { cn } from '@/lib/cn'

type StyleKey =
  | 'styleSwingFx'
  | 'styleCryptoMomentum'
  | 'styleLowRisk'
  | 'styleScalping'
  | 'styleMacroMetals'

interface Trader {
  id: string
  name: string
  handle: string
  avatar: string
  roi30d: number
  winRate: number
  followers: number
  aum: number
  risk: 1 | 2 | 3 | 4 | 5
  styleKey: StyleKey
  copying: boolean
  allocation?: number
  spark: number[]
}

const MOCK_TRADERS: Trader[] = [
  {
    id: 't1',
    name: 'Nova Capital',
    handle: '@nova_fx',
    avatar: 'N',
    roi30d: 0.184,
    winRate: 0.68,
    followers: 1240,
    aum: 2_450_000,
    risk: 3,
    styleKey: 'styleSwingFx',
    copying: true,
    allocation: 2500,
    spark: [12, 14, 13, 16, 18, 17, 21, 24, 22, 26],
  },
  {
    id: 't2',
    name: 'Alpha Pulse',
    handle: '@alphapulse',
    avatar: 'A',
    roi30d: 0.312,
    winRate: 0.61,
    followers: 3890,
    aum: 8_120_000,
    risk: 4,
    styleKey: 'styleCryptoMomentum',
    copying: false,
    spark: [8, 10, 15, 12, 18, 22, 20, 28, 30, 34],
  },
  {
    id: 't3',
    name: 'Steady Yield',
    handle: '@steady_y',
    avatar: 'S',
    roi30d: 0.072,
    winRate: 0.74,
    followers: 890,
    aum: 1_050_000,
    risk: 2,
    styleKey: 'styleLowRisk',
    copying: true,
    allocation: 1000,
    spark: [10, 10.5, 11, 10.8, 11.2, 11.5, 11.4, 11.8, 12, 12.2],
  },
  {
    id: 't4',
    name: 'Iron Desk',
    handle: '@iron_desk',
    avatar: 'I',
    roi30d: -0.041,
    winRate: 0.52,
    followers: 560,
    aum: 420_000,
    risk: 5,
    styleKey: 'styleScalping',
    copying: false,
    spark: [20, 18, 19, 16, 17, 15, 14, 16, 13, 12],
  },
  {
    id: 't5',
    name: 'Horizon Macro',
    handle: '@horizon_m',
    avatar: 'H',
    roi30d: 0.128,
    winRate: 0.65,
    followers: 2100,
    aum: 5_600_000,
    risk: 3,
    styleKey: 'styleMacroMetals',
    copying: false,
    spark: [14, 15, 14, 16, 18, 17, 19, 20, 19, 22],
  },
]

type Tab = 'discover' | 'following'
type SortKey = 'roi' | 'followers' | 'risk'

/**
 * Copy Trade — full-bleed terminal layout (matches Deposit / Settings density).
 */
export function CopyTradePage({ className }: { className?: string }) {
  const { t } = useTranslation('common')
  const { pushToast } = useToast()
  const [tab, setTab] = useState<Tab>('discover')
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<SortKey>('roi')
  const [traders, setTraders] = useState(MOCK_TRADERS)
  const [followTarget, setFollowTarget] = useState<Trader | null>(null)
  const [allocation, setAllocation] = useState('1000')
  const [unfollowTarget, setUnfollowTarget] = useState<Trader | null>(null)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    let list = traders
    if (tab === 'following') list = list.filter((x) => x.copying)
    if (q) {
      list = list.filter(
        (x) =>
          x.name.toLowerCase().includes(q) ||
          x.handle.toLowerCase().includes(q) ||
          t(`copyTrade.${x.styleKey}`).toLowerCase().includes(q),
      )
    }
    list = [...list].sort((a, b) => {
      if (sort === 'followers') return b.followers - a.followers
      if (sort === 'risk') return a.risk - b.risk
      return b.roi30d - a.roi30d
    })
    return list
  }, [traders, tab, query, sort, t])

  const stats = useMemo(() => {
    const copying = traders.filter((x) => x.copying)
    const allocated = copying.reduce((s, x) => s + (x.allocation ?? 0), 0)
    const avgRoi =
      copying.length === 0
        ? 0
        : copying.reduce((s, x) => s + x.roi30d, 0) / copying.length
    return { count: copying.length, allocated, avgRoi, copying }
  }, [traders])

  const featured = useMemo(
    () => [...traders].sort((a, b) => b.roi30d - a.roi30d)[0],
    [traders],
  )

  return (
    <div
      className={cn(
        'terminal-scroll flex h-full min-h-0 flex-col overflow-y-auto',
        className,
      )}
    >
      <PageHero
        tone="info"
        glow="start"
        eyebrow={t('copyTrade.eyebrow')}
        title={t('sidebar.copy_trade')}
        subtitle={t('copyTrade.subtitle')}
      >
        <div className="grid grid-cols-3 gap-2 lg:max-w-none lg:grid-cols-3 xl:w-full xl:max-w-2xl">
          <HeroStat
            label={t('copyTrade.statFollowing')}
            value={String(stats.count)}
          />
          <HeroStat
            label={t('copyTrade.statAllocated')}
            value={formatCurrency(stats.allocated, 'USD')}
          />
          <HeroStat
            label={t('copyTrade.statAvgRoi')}
            value={formatPercentChange(stats.avgRoi)}
            tone={stats.avgRoi >= 0 ? 'up' : 'down'}
          />
        </div>
      </PageHero>

      <div className="grid flex-1 gap-0 lg:grid-cols-[minmax(0,1fr)_16rem]">
        <div className="min-w-0 space-y-3 border-e border-terminal-border/30 p-3 sm:p-4">
          {featured ? (
            <div className="flex flex-wrap items-center gap-3 rounded border border-info/25 bg-info/[0.06] p-3 sm:p-3.5">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded bg-info text-sm font-bold text-white">
                {featured.avatar}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-info">
                  {t('copyTrade.roi30d')}
                </p>
                <p className="truncate text-sm font-semibold text-terminal-fg">
                  {featured.name}{' '}
                  <span className="font-normal text-terminal-muted">
                    {featured.handle}
                  </span>
                </p>
              </div>
              <Sparkline values={featured.spark} up={featured.roi30d >= 0} />
              <p
                className={cn(
                  'font-mono text-lg font-bold tabular-nums',
                  featured.roi30d >= 0 ? 'text-bid' : 'text-danger',
                )}
              >
                {formatPercentChange(featured.roi30d)}
              </p>
              <button
                type="button"
                onClick={() => {
                  if (featured.copying) setUnfollowTarget(featured)
                  else {
                    setFollowTarget(featured)
                    setAllocation('1000')
                  }
                }}
                className={cn(
                  'h-8 rounded px-3 text-[11px] font-semibold text-white',
                  featured.copying
                    ? 'bg-danger hover:bg-danger-muted'
                    : 'bg-info hover:bg-info-muted',
                )}
              >
                {featured.copying
                  ? t('copyTrade.stopCopy')
                  : t('copyTrade.copy')}
              </button>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded border border-terminal-border/40 bg-terminal-elevated/40 p-0.5">
              {(['discover', 'following'] as const).map((id) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTab(id)}
                  className={cn(
                    'h-8 rounded-sm px-3 text-xs font-semibold',
                    tab === id
                      ? 'bg-info text-white'
                      : 'text-terminal-muted hover:text-terminal-fg',
                  )}
                >
                  {id === 'discover'
                    ? t('copyTrade.tabDiscover')
                    : t('copyTrade.tabFollowing')}
                </button>
              ))}
            </div>
            <div className="relative min-w-[10rem] flex-1">
              <span className="pointer-events-none absolute inset-y-0 start-2.5 flex items-center text-terminal-muted">
                <SearchIcon />
              </span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('copyTrade.search')}
                className={cn(
                  'h-8 w-full rounded border border-terminal-border/40 bg-terminal-surface',
                  'ps-8 pe-3 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-info',
                )}
              />
            </div>
            {(
              [
                ['roi', t('copyTrade.roi30d')],
                ['followers', t('copyTrade.followers')],
                ['risk', t('copyTrade.risk')],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setSort(id)}
                className={cn(
                  'h-8 rounded px-2 text-[10px] font-semibold',
                  sort === id
                    ? 'bg-info/15 text-info'
                    : 'bg-terminal-elevated text-terminal-muted hover:text-terminal-fg',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((trader) => (
              <TraderCard
                key={trader.id}
                trader={trader}
                onCopy={() => {
                  setFollowTarget(trader)
                  setAllocation('1000')
                }}
                onStop={() => setUnfollowTarget(trader)}
              />
            ))}
            {filtered.length === 0 ? (
              <div className="col-span-full rounded border border-dashed border-terminal-border/50 px-4 py-12 text-center text-xs text-terminal-muted">
                {t('copyTrade.empty')}
              </div>
            ) : null}
          </div>
        </div>

        <aside className="hidden border-t border-terminal-border/30 bg-terminal-elevated/20 p-3 lg:block lg:border-t-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-info">
            {t('copyTrade.tabFollowing')}
          </p>
          <p className="mt-1 text-sm font-semibold text-terminal-fg">
            {stats.count} · {formatCurrency(stats.allocated, 'USD')}
          </p>
          <ul className="mt-3 space-y-1.5">
            {stats.copying.map((x) => (
              <li
                key={x.id}
                className="flex items-center gap-2 rounded border border-terminal-border/30 bg-terminal-surface px-2 py-1.5"
              >
                <span className="inline-flex h-7 w-7 items-center justify-center rounded bg-info/15 text-[11px] font-bold text-info">
                  {x.avatar}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[11px] font-semibold">
                    {x.name}
                  </span>
                  <span
                    className={cn(
                      'font-mono text-[10px]',
                      x.roi30d >= 0 ? 'text-bid' : 'text-danger',
                    )}
                  >
                    {formatPercentChange(x.roi30d)}
                  </span>
                </span>
              </li>
            ))}
            {stats.copying.length === 0 ? (
              <li className="py-6 text-center text-[11px] text-terminal-muted">
                {t('copyTrade.empty')}
              </li>
            ) : null}
          </ul>
        </aside>
      </div>

      <FormModal
        open={!!followTarget}
        title={t('copyTrade.followTitle')}
        description={
          followTarget
            ? t('copyTrade.followDesc', { name: followTarget.name })
            : undefined
        }
        confirmLabel={t('copyTrade.copy')}
        disabled={!(Number(allocation) > 0)}
        onCancel={() => setFollowTarget(null)}
        onConfirm={() => {
          if (!followTarget) return
          const amt = Number(allocation)
          setTraders((list) =>
            list.map((x) =>
              x.id === followTarget.id
                ? { ...x, copying: true, allocation: amt }
                : x,
            ),
          )
          setFollowTarget(null)
          pushToast({ tone: 'success', title: t('copyTrade.followSuccess') })
        }}
      >
        <ModalField label={t('copyTrade.allocation')}>
          <input
            className={modalInputClass}
            value={allocation}
            onChange={(e) => setAllocation(e.target.value)}
            inputMode="decimal"
          />
        </ModalField>
      </FormModal>

      <ConfirmModal
        open={!!unfollowTarget}
        title={t('copyTrade.stopTitle')}
        message={
          unfollowTarget
            ? t('copyTrade.stopMessage', { name: unfollowTarget.name })
            : ''
        }
        tone="danger"
        confirmLabel={t('copyTrade.stopCopy')}
        onCancel={() => setUnfollowTarget(null)}
        onConfirm={() => {
          if (!unfollowTarget) return
          setTraders((list) =>
            list.map((x) =>
              x.id === unfollowTarget.id
                ? { ...x, copying: false, allocation: undefined }
                : x,
            ),
          )
          setUnfollowTarget(null)
          pushToast({ tone: 'success', title: t('copyTrade.stopSuccess') })
        }}
      />
    </div>
  )
}

function HeroStat({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'up' | 'down'
}) {
  return (
    <div className="rounded border border-info/20 bg-terminal-surface/90 px-2.5 py-2">
      <p className="text-[9px] font-semibold uppercase tracking-wider text-terminal-muted">
        {label}
      </p>
      <p
        className={cn(
          'mt-0.5 font-mono text-xs font-semibold tabular-nums sm:text-sm',
          tone === 'up' && 'text-bid',
          tone === 'down' && 'text-danger',
          !tone && 'text-terminal-fg',
        )}
      >
        {value}
      </p>
    </div>
  )
}

function TraderCard({
  trader,
  onCopy,
  onStop,
}: {
  trader: Trader
  onCopy: () => void
  onStop: () => void
}) {
  const { t } = useTranslation('common')
  return (
    <article
      className={cn(
        'flex flex-col rounded border bg-terminal-surface p-3',
        trader.copying
          ? 'border-info/35 shadow-[inset_2px_0_0_0_var(--color-info)]'
          : 'border-terminal-border/40',
      )}
    >
      <div className="flex items-start gap-2.5">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded bg-info/12 text-xs font-bold text-info">
          {trader.avatar}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h2 className="truncate text-[13px] font-semibold text-terminal-fg">
              {trader.name}
            </h2>
            {trader.copying ? (
              <span className="shrink-0 rounded bg-info/15 px-1 py-px text-[9px] font-semibold text-info">
                {t('copyTrade.tabFollowing')}
              </span>
            ) : null}
          </div>
          <p className="truncate text-[10px] text-terminal-muted">
            {trader.handle} · {t(`copyTrade.${trader.styleKey}`)}
          </p>
        </div>
        <Sparkline values={trader.spark} up={trader.roi30d >= 0} />
      </div>

      <div className="mt-3 grid grid-cols-4 gap-1.5 border-t border-terminal-border/30 pt-2.5 text-[10px]">
        <Metric
          label={t('copyTrade.roi30d')}
          value={formatPercentChange(trader.roi30d)}
          tone={trader.roi30d >= 0 ? 'up' : 'down'}
        />
        <Metric
          label={t('copyTrade.winRate')}
          value={formatPercentChange(trader.winRate)}
        />
        <Metric
          label={t('copyTrade.followers')}
          value={formatNumber(trader.followers)}
        />
        <Metric label="AUM" value={formatCompact(trader.aum)} />
      </div>

      <div className="mt-2.5 flex items-center justify-between gap-2">
        <RiskMeter risk={trader.risk} label={t('copyTrade.risk')} />
        {trader.copying ? (
          <button
            type="button"
            onClick={onStop}
            className="h-7 rounded border border-danger/40 px-2.5 text-[10px] font-semibold text-danger hover:bg-danger/10"
          >
            {t('copyTrade.stopCopy')}
          </button>
        ) : (
          <button
            type="button"
            onClick={onCopy}
            className="h-7 rounded bg-info px-2.5 text-[10px] font-semibold text-white hover:bg-info-muted"
          >
            {t('copyTrade.copy')}
          </button>
        )}
      </div>
      {trader.copying && trader.allocation ? (
        <p className="mt-2 text-[10px] text-terminal-muted">
          {t('copyTrade.yourAllocation')}:{' '}
          <span className="font-mono font-semibold text-terminal-fg">
            {formatCurrency(trader.allocation, 'USD')}
          </span>
        </p>
      ) : null}
    </article>
  )
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'up' | 'down'
}) {
  return (
    <div>
      <p className="truncate text-terminal-muted">{label}</p>
      <p
        className={cn(
          'font-mono text-[11px] font-semibold tabular-nums text-terminal-fg',
          tone === 'up' && 'text-bid',
          tone === 'down' && 'text-danger',
        )}
      >
        {value}
      </p>
    </div>
  )
}

function RiskMeter({ risk, label }: { risk: number; label: string }) {
  return (
    <div className="min-w-0 flex-1">
      <div className="mb-1 flex justify-between text-[9px] text-terminal-muted">
        <span>{label}</span>
        <span className="font-mono font-semibold text-terminal-fg">
          {risk}/5
        </span>
      </div>
      <div className="flex gap-0.5">
        {Array.from({ length: 5 }, (_, i) => (
          <span
            key={i}
            className={cn(
              'h-1 flex-1 rounded-sm',
              i < risk
                ? risk >= 4
                  ? 'bg-danger'
                  : risk >= 3
                    ? 'bg-info'
                    : 'bg-bid'
                : 'bg-terminal-elevated',
            )}
          />
        ))}
      </div>
    </div>
  )
}

function Sparkline({ values, up }: { values: number[]; up: boolean }) {
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const w = 56
  const h = 22
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w
      const y = h - ((v - min) / range) * (h - 4) - 2
      return `${x},${y}`
    })
    .join(' ')

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-[22px] w-14 shrink-0" aria-hidden>
      <polyline
        fill="none"
        stroke={up ? 'var(--color-bid)' : 'var(--color-danger)'}
        strokeWidth="1.6"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={points}
      />
    </svg>
  )
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden>
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M10.5 10.5L14 14"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}
